import { loadShips } from "../../data/loadShips";
import { MARKET_ITEMS, type MarketCategory } from "../../data/marketItems";
import { BASE_CR_PER_M3, RESOURCE_TIER } from "../../resourceCatalog";
import { fastTravelFuelCost, warpFuelCost } from "../../fuel";
import { provisionsPerHour } from "../../provisions";
import type { NpcHullTierLabel } from "../../types";

/**
 * Every word of player-facing help in one place (Don's direction): the FAQ,
 * a glossary, a ship overview and an item listing with real descriptions.
 *
 * Written as data rather than markup so the FAQ pane and the new-captain
 * tutorial can present the same facts differently without either drifting
 * from the other -- and so the ship and item sections are GENERATED from the
 * real roster and the real Market table. A hand-written item list would be
 * wrong the first time an item is renamed or repriced, which has already
 * happened once (16.88 renamed ten of them).
 */

export interface HelpEntry {
  q: string;
  a: string;
}

export interface HelpSection {
  title: string;
  entries: HelpEntry[];
}

/**
 * Fuel and Provisions numbers, generated from the real models (16.104).
 *
 * Same reason the ship and item sections are generated: a hand-written figure is
 * wrong the first time the model is tuned, and both of these are dev-tunable
 * (16.65). The previous hand-written Fuel answer proved the point -- it said
 * "moving inside a system costs nothing", which was simply false, since a Fast
 * Travel is inside a system and costs fuel. Nobody caught it because nothing tied
 * the sentence to the code.
 */
const FUEL_COST_TEXT = (() => {
  const rows = (["Small", "Medium", "Large", "Capital"] as NpcHullTierLabel[]).map(
    (t) => `${t} ${fastTravelFuelCost(t)}/${warpFuelCost(t)}`
  );
  return `Costs scale with hull size (Fast Travel/Warp): ${rows.join(", ")}.`;
})();

const PROVISIONS_PER_CREW_HOUR_TEXT = (() => {
  // Expressed as crew-per-provision rather than provisions-per-crew: "1
  // Provision feeds 10 crew for an hour" is the shape a player can hold in
  // their head, and it is the shape Section 11 states the rule in.
  const perCrewHour = provisionsPerHour(1);
  const crewPerProvision = perCrewHour > 0 ? Math.round(1 / perCrewHour) : 0;
  return `One Provision feeds about ${crewPerProvision} crew for an hour of clock time.`;
})();

// ---------------------------------------------------------------------------
// FAQ
// ---------------------------------------------------------------------------

export const FAQ_SECTIONS: HelpSection[] = [
  {
    title: "Getting started",
    entries: [
      {
        q: "What am I actually doing in this game?",
        a: "You are a captain, not a pilot. You give orders — move, orbit, mine, fire, loot, dock — and your crew carries them out. You take missions, haul cargo, mine asteroids, fight or avoid opponents, and spend what you earn on better ships and modules. There is no win condition; the goal is to build a fleet and a bank balance worth having."
      },
      {
        q: "How do I move?",
        a: "Tap an object or empty space to select it, then use Move To. Orbit holds a set distance instead of flying straight at the target, which is how you fight and mine without drifting into things. Tapping empty space drops a marker you can Move To, so you are never limited to flying between objects."
      },
      {
        q: "How do I make money at the start?",
        a: "Three reliable ways. Mine asteroids and sell the ore at a Station. Take Mission Board contracts — hauling and delivery pay without needing to fight. Loot wrecks: anything you destroy leaves one, and salvage sells. Trading between systems pays best once you know the prices, but it needs capital first."
      },
      {
        q: "Why can I only pick a Tier 1 ship?",
        a: "Your first hull is free, so it is a starter hull. Tiers 2 to 4 are bought at a Station Shipyard once you can afford them. Pick the role you want to play, not the biggest number — a Tier 1 miner earns far more than a Tier 1 gunship sitting in an empty asteroid field."
      },
      {
        q: "Do I have to log in?",
        a: "No. The game is fully playable and saves to your browser without an account. Signing in adds cloud sync so the same captain follows you between devices, and nothing else."
      }
    ]
  },
  {
    title: "Ships and modules",
    entries: [
      {
        q: "What do the six roles do?",
        a: "Digger mines. Packmule hauls. Journeyman is the generalist that does a bit of everything. Kestrel is fast and fragile. Marauder is the heavy brawler. Wildcat trades protection for firepower. Every role exists at all four tiers, so a role is a job, not a power level."
      },
      {
        q: "What are hull tiers?",
        a: "T1 Small, T2 Medium, T3 Large, T4 Capital. Each step up means more hull, more shield, more cargo and more crew — and a much larger bill, plus more Provisions burned per hour. A bigger hull you cannot feed is worse than a smaller one you can."
      },
      {
        q: "What is the difference between Cargo and the Ore Hold?",
        a: "Two separate holds. The Ore Hold is the only place mined resources go. Cargo carries everything else: wreck salvage, trade goods, mission containers. A mining hull pairs a large ore hold with a small cargo bay, so it can fill up on ore and still have nowhere to put a wreck's contents."
      },
      {
        q: "How do modules work?",
        a: "Hardpoints hold weapons and the Mining Laser; they cycle continuously while a valid target is in range. Utility slots hold everything else — shields, repair, scanners, drones, cargo expanders, cloak, command. A module is either off, armed, cycling, or out of range, and the ring around it shows which."
      },
      {
        q: "What does the Mining Laser need?",
        a: "An asteroid selected and inside its range, plus room in your Ore Hold. It channels continuously rather than firing in bursts, and stops the moment the node is empty or the hold is full."
      },
      {
        q: "Do drones help?",
        a: "Yes, and their capacity is about the size of drone a bay can hold rather than the number. A hull carries a small deployed complement plus spares, because drones are lost in combat and a bay with no spares stops contributing partway through a fight."
      }
    ]
  },
  {
    title: "Combat",
    entries: [
      {
        q: "How does combat work?",
        a: "Select a target, arm your weapons, and they cycle automatically while the target stays in range. Damage goes to shields first, then hull. At zero hull a ship is destroyed and leaves a wreck. You do not aim — position and range are the decisions you actually make."
      },
      {
        q: "Should I be fighting at all?",
        a: "Often not. Fleeing is a real option and costs nothing but time. Repairs cost resources, and a fight you barely win can cost more than the wreck is worth. Mining and hauling never shoot back."
      },
      {
        q: "How do I repair?",
        a: "Three ways, and only three. A Repair module fitted to the ship, which consumes Ferrite Ore. A Station Drydock, which charges credits and resources. A Base Repair Bay, which charges resources only. Damage is persistent — it does not quietly heal when you leave the Expedition View."
      },
      {
        q: "What happens if I am destroyed?",
        a: "You wake up in an Escape Pod. You keep your captain, your credits and anything stored at a Base, but the hull and everything in its holds are gone. This is the real reason to keep a second ship in the fleet."
      },
      {
        q: "What is the wreck that appears when I kill something?",
        a: "Loot. Select it and press Loot to open the transfer window; take what fits or transfer everything. Whatever you leave stays aboard the wreck for a return trip. Destroy a ship you had selected and the wreck is selected for you automatically."
      }
    ]
  },
  {
    title: "Crew, fuel and time",
    entries: [
      {
        q: "What are Provisions?",
        a: "Food and supplies for your crew, burned continuously while the clock runs. Run out and the crew's patience runs out with it — far enough and they mutiny and take the ship home without you. Bigger hulls carry bigger crews and burn Provisions faster."
      },
      {
        q: "How fast do Provisions actually run out?",
        a: `${PROVISIONS_PER_CREW_HOUR_TEXT} A full tank is about a day of clock time at whatever crew you carry, so a big hull does not last longer than a small one — it just costs more to fill. Only the crew of the ship you are FLYING eat: stored hulls and hired escorts cost you nothing while you are not aboard them. You can read the exact rate and hours remaining on the Fleet Roster, in Crew Quarters, in the Cargo screen, and in the Fleet Health Box while you fly.`
      },
      {
        q: "What is Fuel for?",
        a: `Both kinds of travel. A Fast Travel across the system you are already in costs fuel, and a Warp through a Gate to another system costs five times as much. ${FUEL_COST_TEXT} Only Move To, orbiting, scanning, mining and combat are free — so you can fight and work all day on an empty tank, and simply not be able to leave.`
      },
      {
        q: "How do I avoid getting stranded?",
        a: "The Fast Travel and Warp buttons both show what the jump costs and how many jumps you will have left after it — if that reads LAST JUMP, the next place you go is the last one you can reach. Undocking while short on fuel or provisions asks you to confirm first, and offers to take you to the Market. If you do strand yourself, the Distress Signal on the Engine module calls for a rescue, but a rescuer's fuel is sold at a steep markup."
      },
      {
        q: "Where do I restock?",
        a: "Any Station Market — Fuel and Provisions are always in stock everywhere, at every station, and neither takes up cargo space. At your own Base you can also pull them from your own stockpile through the Cargo screen."
      },
      {
        q: "Does time pass when I am not playing?",
        a: "Only while you are actually in a View. Returning to the Main Menu pauses the clock, and so does docking at a Base or Station. Closing the tab pauses it too — so Provisions are safe while you are away."
      }
    ]
  },
  {
    title: "The wider galaxy",
    entries: [
      {
        q: "How do I find new systems?",
        a: "The Space Map shows what you can currently detect, and your scanner tier decides how much that is. Ten systems stay hidden until a good enough scanner finds them; simply arriving somewhere converts it to fully known."
      },
      {
        q: "What is faction standing?",
        a: "How each faction feels about you, moved by what you kill and who you trade with. Friendly standing opens better prices — including a discount on Contraband from pirates who consider you one of their own."
      },
      {
        q: "What is a Base?",
        a: "A leased planet you own. It gives you storage that is not aboard any ship, and a Repair Bay that charges resources instead of credits. The lease is a flat purchase, and storage there survives losing a hull."
      },
      {
        q: "What is the Trophy Room?",
        a: "Your record. It collects a discovery entry for each hidden system you find and an opponent codex entry for each type you have met — a reason to go looking rather than to grind."
      }
    ]
  }
];

// ---------------------------------------------------------------------------
// Glossary
// ---------------------------------------------------------------------------

export const GLOSSARY: HelpEntry[] = [
  { q: "Captain", a: "You. You issue orders; you never fly the ship yourself." },
  { q: "Crew", a: "The people aboard. They consume Provisions and they are why mutiny is possible." },
  { q: "Expedition View", a: "The live space view where flying, mining and fighting happen." },
  { q: "Location", a: "One region of space inside a system, 22,500 m in radius. The Mini-map shows all of it; the main view shows the part your zoom covers." },
  { q: "System", a: "A star system containing Locations, planets and usually a Station. Travel between systems costs Fuel." },
  { q: "Hardpoint", a: "A slot for a weapon or the Mining Laser. Cycles automatically at a target in range." },
  { q: "Utility slot", a: "A slot for a non-weapon module: shield, repair, scanner, drones, cargo expander, cloak, command." },
  { q: "Hull HP", a: "Structural health. At zero the ship is destroyed. Does not regenerate on its own." },
  { q: "Shield HP", a: "Absorbs damage before the hull takes any." },
  { q: "EHP", a: "Effective HP — hull plus shield, the single number for how much punishment a hull absorbs." },
  { q: "DPS", a: "Damage per second, the comparable measure of a hull's output." },
  { q: "Ore Hold", a: "The dedicated hold mined resources go into. Separate from Cargo." },
  { q: "Cargo (m3)", a: "The general hold: salvage, trade goods, mission containers. Measured in cubic metres." },
  { q: "Node", a: "An asteroid or deposit with a finite pool of one resource. Richness decides how much." },
  { q: "Richness", a: "A node's yield multiplier: Poor, Standard, Rich or Exceptional." },
  { q: "Wreck", a: "What a destroyed ship leaves. Looted whole via the Loot action, not mined." },
  { q: "Salvage", a: "The common category of goods pulled from wrecks." },
  { q: "Artifact", a: "The rarest category — alien and precursor finds, and the most valuable per cubic metre." },
  { q: "Contraband", a: "Illegal goods. Valuable, and cheaper to buy from pirates who like you." },
  { q: "Provisions", a: "Crew supplies, consumed as time passes. Running out leads to mutiny." },
  { q: "Fuel", a: "Consumed travelling between systems, never within one." },
  { q: "Mutiny", a: "What an unfed crew eventually does: they take the ship back to port without you." },
  { q: "Escape Pod", a: "Where you end up when your hull is destroyed. Captain and credits survive; the ship does not." },
  { q: "Base", a: "A leased planet you own, with storage and a resource-only Repair Bay." },
  { q: "Station", a: "A dockable hub: Market, Shipyard, Drydock repairs." },
  { q: "Drydock", a: "Station repairs, paid in credits plus resources." },
  { q: "Standing", a: "A faction's opinion of you, changed by your actions and affecting prices." },
  { q: "Scanner tier", a: "How far and how well you detect things, including which hidden systems you can find." },
  { q: "Fleet", a: "Every hull you own. Escorts can fly with you; spares wait docked." },
  { q: "Stance", a: "An escort's standing orders — how aggressively it acts without being told." },
  { q: "Tractor Beam", a: "Pulls lootable wrecks within range toward you instead of flying to each one." }
];

// ---------------------------------------------------------------------------
// Ship overview -- generated from the real roster
// ---------------------------------------------------------------------------

export interface RoleOverview {
  role: string;
  blurb: string;
  tiers: Array<{ tier: string; ehp: number; dps: number; cargoM3: number; oreHoldM3: number; crew: number; costCr: number }>;
}

const ROLE_BLURBS: Record<string, string> = {
  Digger: "The miner. Largest ore hold in the game and the reason to fit a Mining Laser at all. Almost no weapons — it earns by never being shot at.",
  Packmule: "The hauler. Built around cargo volume for trade goods and mission containers, with enough hull to survive a bad jump.",
  Journeyman: "The generalist. Middling at everything, bad at nothing, and the least punishing hull to learn the game in.",
  Kestrel: "Fast and thin. Gets somewhere first and leaves before the damage lands. Rewards judgement and punishes a slow decision.",
  Marauder: "The brawler. The heaviest hull and shield in its tier, slow enough that a fight it starts is a fight it finishes.",
  Wildcat: "Guns over armour. The most damage per tier, with the least left over to absorb any of it coming back."
};

export function roleOverviews(): RoleOverview[] {
  const ships = loadShips();
  const roles = [...new Set(ships.map((s) => s.role))];
  return roles.map((role) => {
    // One representative variant per tier -- the roster carries four
    // variants of each, and listing all sixteen per role would bury the
    // comparison this table exists to make.
    const tiers = ["T1", "T2", "T3", "T4"].map((tier) => {
      const s = ships.find((x) => x.role === role && x.hullTier === tier && x.variant === "Standard")
        ?? ships.find((x) => x.role === role && x.hullTier === tier)!;
      return {
        tier,
        ehp: s.hullHp + s.shieldHp,
        dps: s.dps,
        cargoM3: s.cargoM3,
        oreHoldM3: s.oreHoldM3,
        crew: s.crew,
        costCr: s.costCr
      };
    });
    return { role, blurb: ROLE_BLURBS[role] ?? "", tiers };
  });
}

// ---------------------------------------------------------------------------
// Items -- generated from the real Market table and resource catalogue
// ---------------------------------------------------------------------------

export const CATEGORY_BLURBS: Record<MarketCategory, string> = {
  Contraband: "Illegal, valuable and awkward to move. Pirates who count you a friend sell it cheaper than anyone else.",
  Luxury: "Goods bought for status rather than use. High value per cubic metre, and worth carrying a long way.",
  Industrial: "Bulk feedstock. Cheap per cubic metre, so it only pays in volume — hauler cargo, not gunship cargo.",
  Tech: "Manufactured components. Solid value without the legal risk of Contraband.",
  Medical: "Pharmaceuticals and clinical supplies. Steady demand, respectable margins.",
  Provisions: "Food and crew supplies. The cheapest category, and the one that keeps your crew from mutinying.",
  Salvage: "What wrecks are made of. The most common loot you will see, and the first cargo most captains sell.",
  Artifact: "Alien and precursor finds. The rarest and most valuable category per cubic metre; better opponents drop it more often.",
  Munitions: "Weapons and ordnance. Mid-value, and always wanted somewhere."
};

export interface ItemGroup {
  category: MarketCategory;
  blurb: string;
  /** Cheapest and dearest item in the category, in CR per unit. Equal for
   *  every category except Munitions -- see below. */
  minPrice: number;
  maxPrice: number;
  items: Array<{ name: string; pricePerUnit: number }>;
}

/**
 * Trade goods grouped by category, each item carrying its OWN price.
 *
 * Most categories price every item identically (each is its category's
 * CR/m3 rate, and 16.88 fixed a unit at 1 m3), so a single category price
 * would usually be right. Munitions is the exception: it holds the four
 * drone types, individually priced 41/69/116/209 CR by tier (16.87). Quoting
 * one number for that category would have been simply false, and stating
 * "every item in a category is worth the same" alongside it doubly so.
 */
export function itemGroups(): ItemGroup[] {
  const byCategory = new Map<MarketCategory, Array<{ name: string; pricePerUnit: number }>>();
  MARKET_ITEMS.forEach((item) => {
    const list = byCategory.get(item.category) ?? [];
    list.push({ name: item.name, pricePerUnit: item.pricePerUnit });
    byCategory.set(item.category, list);
  });
  return [...byCategory.entries()]
    .map(([category, items]) => {
      const prices = items.map((i) => i.pricePerUnit);
      return {
        category,
        blurb: CATEGORY_BLURBS[category],
        minPrice: Math.min(...prices),
        maxPrice: Math.max(...prices),
        items: [...items].sort((a, b) => a.name.localeCompare(b.name))
      };
    })
    .sort((a, b) => b.maxPrice - a.maxPrice);
}

export interface ResourceRow {
  name: string;
  tier: number;
  crPerM3: number;
  note: string;
}

const RESOURCE_NOTES: Record<string, string> = {
  "Ferrite Ore": "The commonest ore in the galaxy, and the one your Repair module burns — worth keeping some aboard even when it is not worth selling.",
  "Aluminum Ore": "Light structural feedstock. Common, cheap, and always moving.",
  "Frozen Volatiles": "Ices and gases mined from cold bodies. Bulk industrial input.",
  "Titanium Ore": "Structural metal for real hulls. The first ore genuinely worth a detour.",
  "Silicate Crystal": "Optical and computing feedstock. Mid-tier and steady.",
  "Duranium Alloy": "A refined alloy that turns up already-formed in rich deposits. Serious money per cubic metre.",
  "Volatile Crystal": "Unstable lattices used in high-energy work. Valuable and found in poor company.",
  "Neutronium Ore": "Degenerate matter from dead stars. Absurdly dense, absurdly valuable, and rare.",
  "Xenite Alloy": "Alien alloy nobody has managed to reproduce. The single most valuable thing you can pull out of a rock."
};

export function resourceRows(): ResourceRow[] {
  return Object.entries(RESOURCE_TIER)
    .map(([name, tier]) => ({
      name,
      tier,
      crPerM3: BASE_CR_PER_M3[tier],
      note: RESOURCE_NOTES[name] ?? ""
    }))
    .sort((a, b) => a.tier - b.tier || a.name.localeCompare(b.name));
}
